import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScreenController } from './controllers.js';

const tabs = [
    { key: 'workspace', label: 'Workspace', path: '/', title: 'Media Workspace', mainId: 'workspace' },
    { key: 'queue', label: 'Queue', path: '/queue.html', title: 'Queue', mainId: 'queue' },
    { key: 'history', label: 'History', path: '/history.html', title: 'History', mainId: 'history' },
    { key: 'more', label: 'More', path: '/more.html', title: 'More', mainId: 'more' },
];

const moreRoutes = [
    { key: 'vault', label: 'Vault', path: '/vault.html', title: 'Vault', mainId: 'vault' },
    { key: 'collections', label: 'Collections', path: '/collections.html', title: 'Collections', mainId: 'collections' },
    { key: 'research', label: 'Research', path: '/research.html', title: 'Research', mainId: 'research' },
    { key: 'organize', label: 'Organize', path: '/organize.html', title: 'Organize', mainId: 'organize' },
    { key: 'admin', label: 'Admin', path: '/admin.html', title: 'Admin', mainId: 'admin' },
    { key: 'settings', label: 'Settings', path: '/settings.html', title: 'Settings', mainId: 'settings' },
    { key: 'account', label: 'Account', path: '/account.html', title: 'Account', mainId: 'account' },
];

const secondaryRoutes = [
    { key: 'inspector', path: '/inspector.html', title: 'Inspector', mainId: 'inspector' },
    { key: 'reset', path: '/reset-password.html', title: 'Reset Password', mainId: 'reset' },
    { key: 'offline', path: '/offline.html', title: 'Offline', mainId: 'offline' },
];

const routeMap = new Map([...tabs, ...moreRoutes, ...secondaryRoutes].map((route) => [route.path, route]));
routeMap.set('/index.html', tabs[0]);

export function App() {
    const [route, setRoute] = useState(() => resolveRoute(window.location.pathname));

    const navigate = useCallback((path, replace = false) => {
        const nextUrl = new URL(path, window.location.origin);
        const nextRoute = resolveRoute(nextUrl.pathname);
        const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        if (replace) {
            window.history.replaceState({}, '', nextPath);
        } else {
            window.history.pushState({}, '', nextPath);
        }
        setRoute(nextRoute);
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const onPopState = () => setRoute(resolveRoute(window.location.pathname));
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    useEffect(() => {
        document.title = route.key === 'workspace' ? 'SaveFlow - Media Workspace by Aliiq' : `${route.title} - SaveFlow`;
    }, [route]);

    useEffect(() => {
        let deferredInstallPrompt = null;

        const onBeforeInstallPrompt = (event) => {
            event.preventDefault();
            deferredInstallPrompt = event;
            window.SaveFlowPWA = {
                canInstall: true,
                promptInstall: async () => {
                    if (!deferredInstallPrompt) {
                        return { outcome: 'unavailable' };
                    }
                    deferredInstallPrompt.prompt();
                    const choice = await deferredInstallPrompt.userChoice;
                    deferredInstallPrompt = null;
                    window.SaveFlowPWA = { canInstall: false, promptInstall: async () => ({ outcome: 'unavailable' }) };
                    return choice;
                },
            };
            window.dispatchEvent(new CustomEvent('saveflow:pwa-install-ready'));
            window.SaveFlowA11y?.announce('SaveFlow is ready to install.');
        };

        const onAppInstalled = () => {
            window.SaveFlowPWA = { canInstall: false, promptInstall: async () => ({ outcome: 'installed' }) };
            window.dispatchEvent(new CustomEvent('saveflow:pwa-installed'));
            window.SaveFlowA11y?.announce('SaveFlow is installed.');
        };

        window.SaveFlowPWA = { canInstall: false, promptInstall: async () => ({ outcome: 'unavailable' }) };
        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onAppInstalled);

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/sw.js').catch(() => {
                window.SaveFlowA11y?.announce('Service worker registration failed. The app can still run in the browser.');
            });
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onAppInstalled);
            delete window.SaveFlowPWA;
        };
    }, []);


    useEffect(() => {
        const onClick = (event) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const link = event.target.closest?.('a[href]');
            if (!link || link.target || link.hasAttribute('download')) {
                return;
            }

            const url = new URL(link.href, window.location.origin);
            if (url.origin !== window.location.origin || !routeMap.has(url.pathname)) {
                return;
            }

            event.preventDefault();
            navigate(`${url.pathname}${url.search}${url.hash}`);
        };

        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [navigate]);

    const activeTab = tabs.find((tab) => tab.key === route.key) || (moreRoutes.some((item) => item.key === route.key) ? tabs.find((tab) => tab.key === 'more') : null);
    const skipLabel = route.key === 'reset' ? 'Skip to reset password' : `Skip to ${route.title.toLowerCase()}`;

    return (
        <A11yProvider navigate={navigate}>
            <a className="skip-link" href={`#${route.mainId}`}>{skipLabel}</a>
            <header className="app-header app-shell-header">
                <div className="brand-lockup">
                    <p className="eyebrow">SaveFlow by Aliiq</p>
                    <h1>{route.title}</h1>
                </div>
                <NativeTabs tabs={tabs} activeTab={activeTab} navigate={navigate} placement="top" />
            </header>

            <main
                id={route.mainId}
                className={`workspace app-main ${route.key === 'settings' ? 'settings-layout' : ''}`}
                role={activeTab ? 'tabpanel' : undefined}
                aria-labelledby={activeTab ? `tab-${activeTab.key}` : undefined}
                tabIndex={-1}
            >
                <RouteScreen key={`${route.key}:${window.location.search}`} route={route} />
            </main>

            <NativeTabs tabs={tabs} activeTab={activeTab} navigate={navigate} placement="bottom" />
            <Footer />
        </A11yProvider>
    );
}

function resolveRoute(pathname) {
    return routeMap.get(pathname) || tabs[0];
}

function NativeTabs({ tabs: items, activeTab, navigate, placement }) {
    const refs = useRef([]);

    function onKeyDown(event, index) {
        const direction = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1,
            Home: 'home',
            End: 'end',
        }[event.key];

        if (!direction) {
            return;
        }

        event.preventDefault();
        const nextIndex = direction === 'home'
            ? 0
            : direction === 'end'
                ? items.length - 1
                : (index + direction + items.length) % items.length;
        refs.current[nextIndex]?.focus();
    }

    return (
        <nav
            className={`top-nav app-tabs native-tabs ${placement === 'bottom' ? 'native-bottom-tabs' : ''}`}
            aria-label={placement === 'bottom' ? 'Primary app tabs' : 'Primary pages'}
            role="tablist"
        >
            {items.map((tab, index) => {
                const selected = activeTab?.key === tab.key;
                return (
                    <button
                        key={tab.key}
                        ref={(node) => { refs.current[index] = node; }}
                        id={`tab-${tab.key}`}
                        className="native-tab"
                        type="button"
                        role="tab"
                        aria-selected={selected ? 'true' : 'false'}
                        aria-controls={`panel-${tab.key}`}
                        tabIndex={selected || (!activeTab && index === 0) ? 0 : -1}
                        onClick={() => navigate(tab.path)}
                        onKeyDown={(event) => onKeyDown(event, index)}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </nav>
    );
}

function RouteScreen({ route }) {
    useScreenController(route.key);

    switch (route.key) {
        case 'workspace':
            return <WorkspaceScreen />;
        case 'queue':
            return <QueueScreen />;
        case 'vault':
            return <VaultScreen />;
        case 'history':
            return <HistoryScreen />;
        case 'more':
            return <MoreScreen />;
        case 'collections':
            return <CollectionsScreen />;
        case 'research':
            return <ResearchScreen />;
        case 'organize':
            return <OrganizeScreen />;
        case 'admin':
            return <AdminScreen />;
        case 'settings':
            return <SettingsScreen />;
        case 'account':
            return <AccountScreen />;
        case 'inspector':
            return <InspectorScreen />;
        case 'reset':
            return <ResetPasswordScreen />;
        case 'offline':
            return <OfflineScreen />;
        default:
            return <WorkspaceScreen />;
    }
}

function A11yProvider({ children, navigate }) {
    const defaults = useMemo(() => ({ enabled: false, simple: false, largeText: false, sound: false, textFirst: false }), []);
    const [state, setState] = useState(() => loadA11y(defaults));
    const [dialog, setDialog] = useState(null);
    const [liveMessage, setLiveMessage] = useState('');
    const returnFocusRef = useRef(null);

    const closeDialog = useCallback((runClose = true) => {
        setDialog((current) => {
            if (runClose) {
                current?.onClose?.();
            }
            const focusTarget = current?.returnFocusTo || returnFocusRef.current;
            window.setTimeout(() => focusTarget?.focus?.(), 0);
            return null;
        });
    }, []);

    const openDialog = useCallback((options) => {
        returnFocusRef.current = options.returnFocusTo || document.activeElement;
        setDialog({ ...options, returnFocusTo: returnFocusRef.current });
    }, []);

    useEffect(() => {
        document.body.classList.toggle('a11y-mode', state.enabled);
        document.body.classList.toggle('simple-mode', state.simple);
        document.body.classList.toggle('large-text-mode', state.largeText);
        document.body.classList.toggle('text-first-mode', state.textFirst);
        localStorage.setItem('saveflow:accessibility', JSON.stringify(state));
    }, [state]);

    useEffect(() => {
        window.SaveFlowA11y = {
            announce(message) {
                setLiveMessage(message);
                if (state.sound && String(message).toLowerCase().includes('ready')) {
                    beep();
                }
            },
            openDrawer({ title, body, actions = [], returnFocusTo = document.activeElement }) {
                openDialog({ title, body, actions, returnFocusTo, className: 'result-drawer', labelledBy: 'result-drawer-title' });
            },
            openActionSheet({ title = 'Actions', actions = [], returnFocusTo = document.activeElement }) {
                openDialog({ title, body: '', actions, returnFocusTo, className: 'action-sheet', labelledBy: 'action-sheet-title' });
            },
            confirm({ title = 'Confirm action', message, confirmLabel = 'Confirm', returnFocusTo = document.activeElement }) {
                return new Promise((resolve) => {
                    openDialog({
                        title,
                        body: `<p>${escapeHtml(message || 'Continue?')}</p>`,
                        className: 'confirm-dialog',
                        labelledBy: 'confirm-title',
                        returnFocusTo,
                        actions: [
                            { label: 'Cancel', kind: 'secondary', onSelect: () => resolve(false) },
                            { label: confirmLabel, onSelect: () => resolve(true) },
                        ],
                        onClose: () => resolve(false),
                    });
                });
            },
        };

        return () => {
            delete window.SaveFlowA11y;
        };
    }, [openDialog, state.sound]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeDialog();
            }

            if (event.key === '?' || (event.ctrlKey && event.key === '/')) {
                event.preventDefault();
                openDialog({
                    title: 'Keyboard Help',
                    body: `
                        <ul class="meta-list">
                            <li><span>?</span> Open keyboard help</li>
                            <li><span>Alt + U</span> Focus the main URL or text field</li>
                            <li><span>Alt + H</span> Open History</li>
                            <li><span>Alt + Q</span> Open Queue</li>
                            <li><span>Alt + V</span> Open Vault</li>
                            <li><span>Alt + C</span> Open Collections</li>
                            <li><span>Alt + O</span> Open Organize</li>
                            <li><span>Alt + A</span> Open Account</li>
                        </ul>
                    `,
                    actions: [],
                    returnFocusTo: document.activeElement,
                    className: 'shortcut-dialog',
                    labelledBy: 'shortcut-title',
                });
            }

            if (!event.altKey) {
                return;
            }

            const target = {
                v: '/vault.html',
                h: '/history.html',
                q: '/queue.html',
                m: '/more.html',
                o: '/organize.html',
                c: '/collections.html',
                s: '/settings.html',
                a: '/account.html',
            }[event.key.toLowerCase()];

            if (target) {
                event.preventDefault();
                navigate(target);
            }

            if (event.key.toLowerCase() === 'u') {
                const input = document.querySelector('#media-url, input[type="url"], textarea');
                if (input) {
                    event.preventDefault();
                    input.focus();
                }
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [closeDialog, navigate, openDialog]);

    return (
        <>
            <div id="saveflow-live-region" className="sr-only" aria-live="polite">{liveMessage}</div>
            {children}
            <AccessibilityToolbar state={state} setState={setState} openDialog={openDialog} />
            {dialog && <ReactDialog dialog={dialog} closeDialog={closeDialog} />}
        </>
    );
}

function AccessibilityToolbar({ state, setState, openDialog }) {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);

    function toggle(name) {
        setState((current) => ({ ...current, [name]: !current[name] }));
    }

    return (
        <div className="a11y-toolbar">
            <button
                ref={buttonRef}
                type="button"
                aria-expanded={open ? 'true' : 'false'}
                aria-controls="a11y-menu-panel"
                onClick={() => setOpen((current) => !current)}
            >
                <span>{open ? 'Close Accessibility' : 'Open Accessibility'}</span>
            </button>
            <div id="a11y-menu-panel" className="a11y-menu" hidden={!open}>
                {[
                    ['enabled', 'Accessibility mode'],
                    ['simple', 'Simple layout'],
                    ['largeText', 'Large text'],
                    ['textFirst', 'Text-first metadata'],
                    ['sound', 'Completion sound'],
                ].map(([name, label]) => (
                    <label key={name}>
                        <input type="checkbox" checked={state[name]} onChange={() => toggle(name)} />
                        {label}
                    </label>
                ))}
                <button
                    type="button"
                    onClick={() => openDialog({
                        title: 'Keyboard Help',
                        body: '<p>Use the Tab key to move through controls. Use arrow keys inside the app tabs.</p>',
                        actions: [],
                        returnFocusTo: buttonRef.current,
                        className: 'shortcut-dialog',
                        labelledBy: 'shortcut-title',
                    })}
                >
                    Keyboard Help
                </button>
            </div>
        </div>
    );
}

function ReactDialog({ dialog, closeDialog }) {
    const closeRef = useRef(null);

    useEffect(() => {
        closeRef.current?.focus();
    }, []);

    return (
        <div className="modal-backdrop saveflow-dialog-backdrop" onClick={(event) => {
            if (event.target === event.currentTarget) {
                closeDialog();
            }
        }}>
            <section className={`saveflow-dialog ${dialog.className}`} role="dialog" aria-modal="true" aria-labelledby={dialog.labelledBy}>
                <div className="section-head">
                    <h2 id={dialog.labelledBy}>{dialog.title}</h2>
                    <button ref={closeRef} type="button" onClick={() => closeDialog()}>Close</button>
                </div>
                <div className="dialog-body" dangerouslySetInnerHTML={{ __html: dialog.body || '' }} />
                <div className="dialog-actions">
                    {(dialog.actions || []).map((action) => {
                        if (action.href) {
                            return <a key={action.label} className={`button-link ${action.kind === 'secondary' ? 'secondary' : ''}`} href={action.href}>{action.label}</a>;
                        }

                        return (
                            <button
                                key={action.label}
                                type="button"
                                className={action.kind === 'secondary' ? 'secondary' : ''}
                                onClick={() => {
                                    closeDialog(false);
                                    action.onSelect?.();
                                }}
                            >
                                {action.label}
                            </button>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

function WorkspaceScreen() {
    return (
        <>
            <section className="flow-panel command-panel workspace-command" aria-labelledby="paste-title">
                <div className="panel-copy">
                    <p className="eyebrow">TikTok, YouTube, Instagram</p>
                    <h2 id="paste-title">Paste a public media link</h2>
                    <p>SaveFlow detects the source, starts a secure job, and returns a signed download link when the backend is configured.</p>
                </div>
                <form id="download-form" className="download-form form-surface">
                    <label htmlFor="media-url">Media URL</label>
                    <div className="input-row">
                        <input id="media-url" name="url" type="url" inputMode="url" autoComplete="url" placeholder="https://www.youtube.com/watch?v=..." required />
                        <button id="paste-clipboard" type="button">Paste</button>
                        <button type="submit">Analyze</button>
                    </div>
                    <p className="hint">Public URLs only. Private, paid, authenticated, and DRM-protected media are out of scope.</p>
                </form>
            </section>
            <section className="status-grid metric-grid two-column pwa-onboarding" aria-label="PWA setup and quick actions">
                <article className="status-card metric-card" id="pwa-onboarding-card">
                    <h2>Install SaveFlow</h2>
                    <p id="pwa-onboarding-status" aria-live="polite">Checking install readiness...</p>
                    <div className="history-actions">
                        <button id="install-saveflow" type="button" hidden>Install App</button>
                        <a className="button-link secondary" href="/settings.html">PWA Health</a>
                    </div>
                </article>
                <article className="status-card metric-card" id="notification-card">
                    <h2>Job Alerts</h2>
                    <p id="notification-status" aria-live="polite">Local completion alerts are optional.</p>
                    <div className="history-actions">
                        <button id="enable-notifications" type="button" hidden>Enable Alerts</button>
                    </div>
                </article>
            </section>
            <section className="status-grid metric-grid workspace-dashboard" aria-label="Download workflow">
                <article className="status-card metric-card" id="detection-card">
                    <h2>Detection</h2>
                    <p id="detection-text">Waiting for a link.</p>
                </article>
                <article className="status-card metric-card" id="job-card">
                    <h2>Job Progress</h2>
                    <div className="progress-shell" aria-hidden="true"><div id="progress-bar" className="progress-bar" /></div>
                    <p id="job-text" aria-live="polite">No job started.</p>
                </article>
                <article className="status-card metric-card result-card" id="result-card">
                    <h2>Result</h2>
                    <div id="result-content"><p>Your download will appear here after processing.</p></div>
                </article>
            </section>
            <section className="history-results section-surface results-surface resume-panel" aria-labelledby="resume-title">
                <div className="section-head section-head-actions">
                    <h2 id="resume-title">Resume Workspace</h2>
                    <div className="history-actions">
                        <button id="process-next-resume" type="button">Process Next</button>
                        <button id="clear-resume" type="button">Clear Session</button>
                    </div>
                </div>
                <p id="resume-status" aria-live="polite">Checking saved workspace state...</p>
                <div id="resume-content" className="history-list result-list" />
            </section>
            <div className="mobile-action-bar" aria-label="Quick actions">
                <button id="mobile-paste" type="button">Paste</button>
                <button id="mobile-analyze" type="button">Analyze</button>
                <button id="mobile-paste-analyze" type="button">Paste + Analyze</button>
            </div>
        </>
    );
}

function HistoryScreen() {
    return (
        <>
            <section className="flow-panel command-panel history-tools" aria-labelledby="history-title">
                <PanelCopy eyebrow="Local workspace" titleId="history-title" title="Find previous jobs">Search completed, failed, and expired jobs from this local SaveFlow database.</PanelCopy>
                <form id="history-form" className="history-form form-surface">
                    <label htmlFor="history-search">Search</label>
                    <input id="history-search" name="q" type="search" placeholder="Title, author, file, or URL" />
                    <div className="filter-row">
                        <label>Platform<Select name="platform" id="platform-filter" options={[['', 'All'], ['youtube', 'YouTube'], ['tiktok', 'TikTok'], ['instagram', 'Instagram']]} /></label>
                        <label>View<Select name="favorite" id="favorite-filter" options={[['', 'All items'], ['true', 'Favorites']]} /></label>
                        <label>Sort<Select name="sort" id="sort-filter" options={[['latest', 'Latest'], ['oldest', 'Oldest'], ['size_desc', 'Largest'], ['size_asc', 'Smallest'], ['platform', 'Platform'], ['type', 'Type']]} /></label>
                    </div>
                    <button type="submit">Search History</button>
                </form>
            </section>
            <ResultsSection titleId="results-title" title="Results" statusId="history-status" status="Loading history...">
                <div className="history-actions">
                    <button id="export-history-csv" type="button">Export CSV</button>
                    <button id="export-history-research-zip" type="button">Export Research ZIP</button>
                    <button id="export-history-media-zip" type="button">Export Media ZIP</button>
                </div>
                <SavedFilterBar scope="history" />
                <div id="history-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function MoreScreen() {
    const items = [
        ['Vault', '/vault.html', 'Save links for later and move them into Queue when ready.'],
        ['Collections', '/collections.html', 'Group completed jobs with notes, tags, and archive exports.'],
        ['Research', '/research.html', 'Review metadata, notes, tags, and exports in a text-first workspace.'],
        ['Organize', '/organize.html', 'Manage recipes, default packages, and local auto-organize rules.'],
        ['Admin', '/admin.html', 'Review owner-only operations, failed jobs, queue health, and audit events.'],
        ['Settings', '/settings.html', 'Check database, download engine, PWA, mail, and platform readiness.'],
        ['Account', '/account.html', 'Log in, manage profile, recovery codes, and active sessions.'],
    ];

    return (
        <>
            <section className="flow-panel command-panel" aria-labelledby="more-title">
                <PanelCopy eyebrow="More tools" titleId="more-title" title="Advanced SaveFlow sections">Core work stays in Workspace, Queue, and History. Use these tools when you need saved links, collections, research, settings, or account controls.</PanelCopy>
            </section>
            <section className="history-results section-surface results-surface" aria-labelledby="more-tools-title">
                <h2 id="more-tools-title">Tools</h2>
                <div className="history-list result-list more-grid">
                    {items.map(([title, href, copy]) => (
                        <article className="history-card more-card" key={href} data-owner-admin-tile={href === '/admin.html' ? 'true' : undefined}>
                            <div>
                                <p className="eyebrow">SaveFlow</p>
                                <h3>{title}</h3>
                                <p>{copy}</p>
                            </div>
                            <div className="history-actions">
                                <a className="button-link" href={href}>Open</a>
                            </div>
                        </article>
                    ))}
                </div>
            </section>
        </>
    );
}

function AdminScreen() {
    return (
        <>
            <section className="flow-panel command-panel" aria-labelledby="admin-title">
                <PanelCopy eyebrow="Owner operations" titleId="admin-title" title="System dashboard">Review SaveFlow health, failed jobs, queue activity, storage usage, cleanup, and security events from the owner account.</PanelCopy>
                <button id="refresh-admin" type="button">Refresh Dashboard</button>
            </section>
            <section className="status-grid metric-grid" aria-label="Admin dashboard metrics">
                <article className="status-card metric-card">
                    <h2>System</h2>
                    <p id="admin-system-status" aria-live="polite">Loading admin summary...</p>
                </article>
                <article className="status-card metric-card">
                    <h2>Jobs</h2>
                    <p id="admin-jobs-status">Waiting for job counts.</p>
                </article>
                <article className="status-card metric-card">
                    <h2>Queue</h2>
                    <p id="admin-queue-status">Waiting for queue counts.</p>
                </article>
                <article className="status-card metric-card">
                    <h2>Storage</h2>
                    <p id="admin-storage-status">Waiting for storage usage.</p>
                </article>
            </section>
            <section className="history-results section-surface results-surface" aria-labelledby="admin-failed-title">
                <div className="section-head section-head-actions">
                    <h2 id="admin-failed-title">Failed Jobs</h2>
                    <form id="admin-failed-form" className="filter-toolbar">
                        <input name="q" type="search" placeholder="Search failures" aria-label="Search failed jobs" />
                        <Select name="platform" options={[['', 'All platforms'], ['youtube', 'YouTube'], ['tiktok', 'TikTok'], ['instagram', 'Instagram']]} />
                        <input name="error_code" type="search" placeholder="Error code" aria-label="Error code" />
                        <button type="submit">Filter</button>
                    </form>
                </div>
                <p id="admin-failed-status" aria-live="polite">Loading failed jobs...</p>
                <div id="admin-failed-list" className="history-list result-list" />
            </section>
            <section className="status-grid metric-grid two-column" aria-label="Operations details">
                <article className="status-card metric-card">
                    <h2>Queue Overview</h2>
                    <p id="admin-queue-overview-status">Loading queue overview...</p>
                    <div id="admin-queue-list" className="history-list result-list compact-list" />
                </article>
                <article className="status-card metric-card">
                    <h2>Audit Events</h2>
                    <p id="admin-audit-status">Loading audit events...</p>
                    <div id="admin-audit-list" className="history-list result-list compact-list" />
                </article>
            </section>
        </>
    );
}

function QueueScreen() {
    return (
        <>
            <section className="flow-panel command-panel" aria-labelledby="queue-title">
                <PanelCopy eyebrow="Batch inbox" titleId="queue-title" title="Add links to process later">Paste one URL per line. Queue processing is manual in this version.</PanelCopy>
                <form id="queue-form" className="history-form form-surface">
                    <label htmlFor="queue-urls">URLs</label>
                    <textarea id="queue-urls" name="urls" placeholder={'https://youtu.be/...\nhttps://www.tiktok.com/@user/video/...'} />
                    <label htmlFor="queue-note">Shared note</label>
                    <input id="queue-note" name="note" type="text" maxLength="500" placeholder="Optional note for these links" />
                    <button type="submit">Add To Queue</button>
                </form>
            </section>
            <ResultsSection titleId="queue-list-title" title="Queue Items" statusId="queue-status" status="Loading queue...">
                <div className="history-actions">
                    <button id="process-next-queue" type="button">Process Next</button>
                    <button id="refresh-queue" type="button">Refresh</button>
                </div>
                <form id="queue-filter-form" className="history-form compact-form form-surface filter-surface">
                    <div className="filter-row">
                        <Select name="status" options={[['', 'All statuses'], ['queued', 'Queued'], ['processing', 'Processing'], ['completed', 'Completed'], ['failed', 'Failed'], ['archived', 'Archived']]} />
                        <PlatformSelect />
                        <button type="submit">Filter</button>
                    </div>
                </form>
                <SavedFilterBar scope="queue" />
                <div id="queue-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function VaultScreen() {
    return (
        <>
            <section className="flow-panel command-panel" aria-labelledby="vault-title">
                <PanelCopy eyebrow="Save for later" titleId="vault-title" title="Link vault">Store useful links before moving them into the queue.</PanelCopy>
                <form id="vault-form" className="history-form form-surface">
                    <label htmlFor="vault-url">URL</label>
                    <input id="vault-url" name="url" type="url" placeholder="https://www.instagram.com/reel/..." />
                    <label htmlFor="vault-note">Note</label>
                    <input id="vault-note" name="note" type="text" maxLength="500" placeholder="Optional note" />
                    <button type="submit">Save Link</button>
                </form>
            </section>
            <ResultsSection titleId="vault-list-title" title="Saved Links" statusId="vault-status" status="Loading vault...">
                <button id="refresh-vault" type="button">Refresh</button>
                <form id="vault-filter-form" className="history-form compact-form form-surface filter-surface">
                    <input name="q" type="search" placeholder="Search URL or note" />
                    <div className="filter-row">
                        <PlatformSelect />
                        <Select name="archived" options={[['false', 'Active'], ['true', 'Archived']]} />
                        <button type="submit">Filter</button>
                    </div>
                </form>
                <SavedFilterBar scope="vault" />
                <div id="vault-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function CollectionsScreen() {
    return (
        <>
            <section className="flow-panel command-panel" aria-labelledby="collections-title">
                <PanelCopy eyebrow="Local library" titleId="collections-title" title="Group saved jobs">Collections organize completed, failed, or expired jobs without moving files from their current storage folders.</PanelCopy>
                <form id="collection-form" className="history-form form-surface">
                    <input type="hidden" name="id" />
                    <label htmlFor="collection-name">Name</label>
                    <input id="collection-name" name="name" type="text" maxLength="120" placeholder="Research clips" />
                    <label htmlFor="collection-description">Description</label>
                    <textarea id="collection-description" name="description" maxLength="800" placeholder="Optional purpose or context" />
                    <label htmlFor="collection-color">Color label</label>
                    <input id="collection-color" name="color_label" type="text" maxLength="32" placeholder="cyan" />
                    <button type="submit">Save Collection</button>
                </form>
            </section>
            <section className="history-results section-surface results-surface" aria-labelledby="collection-list-title">
                <div className="section-head section-head-actions"><h2 id="collection-list-title">Collections</h2></div>
                <form id="collection-filter-form" className="history-form compact-form form-surface filter-surface">
                    <label htmlFor="collection-search">Search</label>
                    <input id="collection-search" name="q" type="search" placeholder="Collection name or description" />
                    <div className="filter-row">
                        <label>Archive<Select name="archived" options={[['', 'Active only'], ['1', 'Include archived']]} /></label>
                        <label>Tag<input name="tag" type="text" maxLength="40" placeholder="research" /></label>
                        <label>Platform<PlatformSelect /></label>
                    </div>
                    <div className="filter-row">
                        <label>Status<Select name="status" options={[['', 'Any'], ['completed', 'Completed'], ['failed', 'Failed'], ['expired', 'Expired']]} /></label>
                        <label>Sort<Select name="sort" options={[['updated', 'Recently updated'], ['name', 'Name'], ['oldest', 'Oldest'], ['items', 'Most items']]} /></label>
                        <button type="submit">Filter Collections</button>
                    </div>
                </form>
                <SavedFilterBar scope="collections" />
                <p id="collection-status" aria-live="polite">Loading collections...</p>
                <div id="collection-list" className="history-list result-list" />
            </section>
            <ResultsSection titleId="collection-detail-title" title="Items" statusId="collection-detail-status" status="Choose a collection to view its items.">
                <div className="history-actions">
                    <button id="export-collection-csv" type="button">Export CSV</button>
                    <button id="export-collection-research-zip" type="button">Export Research ZIP</button>
                    <button id="export-collection-media-zip" type="button">Export Media ZIP</button>
                    <button id="refresh-detail" type="button">Refresh Items</button>
                </div>
                <div id="collection-detail" className="history-list result-list" />
            </ResultsSection>
            <ResultsSection titleId="archive-exports-title" title="Archive Exports" statusId="archive-export-status" status="Loading archive exports...">
                <button id="refresh-exports" type="button">Refresh Exports</button>
                <div id="archive-export-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function ResearchScreen() {
    return (
        <>
            <section className="flow-panel command-panel history-tools" aria-labelledby="research-title">
                <PanelCopy eyebrow="Metadata-first workspace" titleId="research-title" title="Review saved media">Search notes, tags, collections, source URLs, and local metadata without depending on thumbnails.</PanelCopy>
                <form id="research-form" className="history-form form-surface">
                    <label htmlFor="research-search">Search</label>
                    <input id="research-search" name="q" type="search" placeholder="Title, author, note, tag, or URL" />
                    <div className="filter-row">
                        <label>Platform<PlatformSelect /></label>
                        <label>Status<Select name="status" options={[['', 'Any'], ['completed', 'Completed'], ['failed', 'Failed'], ['expired', 'Expired']]} /></label>
                        <label>View<Select name="favorite" options={[['', 'All items'], ['true', 'Favorites']]} /></label>
                    </div>
                    <div className="filter-row">
                        <label>Collection<select id="research-collection-filter" name="collection_id"><option value="">All collections</option></select></label>
                        <label>Tag<input name="tag" type="text" maxLength="40" placeholder="research" /></label>
                        <label>Sort<Select name="sort" options={[['latest', 'Latest'], ['oldest', 'Oldest'], ['title', 'Title'], ['platform', 'Platform'], ['status', 'Status']]} /></label>
                        <button type="submit">Filter Research</button>
                    </div>
                </form>
            </section>
            <ResultsSection titleId="research-actions-title" title="Research Actions" statusId="research-status" status="Loading research workspace...">
                <div className="history-actions">
                    <button id="export-research-csv" type="button">Export CSV</button>
                    <button id="export-research-zip" type="button">Export Research ZIP</button>
                    <button id="export-media-zip" type="button">Export Media ZIP</button>
                </div>
                <div className="filter-row compact-form form-surface filter-surface">
                    <label>Add selected to<select id="research-target-collection"><option value="">Choose collection</option></select></label>
                    <button id="select-visible" type="button">Select Shown</button>
                    <button id="clear-selection" type="button">Clear Selection</button>
                    <button id="add-selected-collection" type="button">Add Selected</button>
                </div>
                <div id="research-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function OrganizeScreen() {
    return (
        <>
            <section className="flow-panel command-panel" aria-labelledby="organize-title">
                <PanelCopy eyebrow="Local rules" titleId="organize-title" title="Auto-organize rules">Rules run locally after jobs, queue items, or vault links are created.</PanelCopy>
                <form id="rule-form" className="history-form form-surface">
                    <input type="hidden" name="id" />
                    <label htmlFor="rule-name">Name</label>
                    <input id="rule-name" name="name" type="text" maxLength="120" placeholder="Favorite completed YouTube videos" />
                    <div className="filter-row">
                        <label>Platform<PlatformSelect name="match_platform" /></label>
                        <label>Status<Select name="match_status" options={[['', 'Any'], ['completed', 'Completed'], ['failed', 'Failed'], ['queued', 'Queued'], ['archived', 'Archived']]} /></label>
                        <label>MIME Prefix<input name="match_mime_prefix" type="text" placeholder="video/" /></label>
                    </div>
                    <div className="checkbox-row">
                        <label><input name="action_favorite" type="checkbox" /> Favorite matching jobs</label>
                        <label><input name="action_archive_queue" type="checkbox" /> Archive matching queue items</label>
                        <label><input name="action_archive_vault" type="checkbox" /> Archive matching vault links</label>
                    </div>
                    <label htmlFor="rule-note">Append note to matching jobs</label>
                    <input id="rule-note" name="action_note" type="text" maxLength="500" placeholder="Optional job note" />
                    <label htmlFor="rule-collection">Add matching jobs to collection</label>
                    <select id="rule-collection" name="action_collection_id"><option value="">No collection action</option></select>
                    <button type="submit">Save Rule</button>
                </form>
            </section>
            <section className="flow-panel command-panel" aria-labelledby="recipes-title">
                <PanelCopy eyebrow="Reusable workflow" titleId="recipes-title" title="Recipes and defaults">Save local export and package preferences for repeat workspace tasks.</PanelCopy>
                <div className="stacked-tools">
                    <PreferencesForm />
                    <RecipeForm />
                </div>
            </section>
            <ResultsSection titleId="rules-title" title="Rules" statusId="rules-status" status="Loading rules...">
                <button id="refresh-rules" type="button">Refresh</button>
                <div id="rules-list" className="history-list result-list" />
            </ResultsSection>
            <ResultsSection titleId="recipe-list-title" title="Recipes" statusId="recipes-status" status="Loading recipes...">
                <button id="refresh-recipes" type="button">Refresh Recipes</button>
                <div id="recipes-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function SettingsScreen() {
    return (
        <>
            <section className="flow-panel command-panel settings-panel" aria-labelledby="system-title">
                <PanelCopy eyebrow="System readiness" titleId="system-title" title="Backend configuration">Use this page to confirm MySQL and the `yt-dlp` binary before running real download jobs.</PanelCopy>
                <button id="refresh-health" type="button">Refresh Status</button>
            </section>
            <section className="status-grid metric-grid two-column" aria-label="System checks">
                {[
                    ['Database', 'database-status'],
                    ['Download Engine', 'ytdlp-status'],
                    ['Retention', 'retention-status'],
                    ['Cleanup', 'cleanup-status'],
                    ['History', 'history-status'],
                    ['PWA Readiness', 'pwa-status'],
                    ['Platform Options', 'platform-status'],
                    ['Cookieless Mode', 'cookieless-status'],
                    ['YouTube', 'youtube-status'],
                    ['TikTok', 'tiktok-status'],
                    ['Instagram', 'instagram-status'],
                    ['Runtime Support', 'runtime-status'],
                    ['Account', 'account-status'],
                    ['Email', 'mail-status'],
                ].map(([title, id]) => <article key={id} className="status-card metric-card"><h2>{title}</h2><p id={id}>Checking...</p></article>)}
            </section>
            <section className="flow-panel command-panel settings-panel" aria-labelledby="pwa-title">
                <PanelCopy eyebrow="PWABuilder checks" titleId="pwa-title" title="Install readiness"><span id="pwa-detail">SaveFlow checks the manifest, service worker, offline page, app icons, app shortcuts, share target, HTTPS, and required app shell assets.</span></PanelCopy>
                <a className="button-link secondary" href="/api/pwa-health.php">Open PWA Health JSON</a>
            </section>
            <section className="flow-panel command-panel settings-panel" aria-labelledby="android-title">
                <PanelCopy eyebrow="Android troubleshooting" titleId="android-title" title="Clear old service worker state after deployment">If Android Chrome keeps reporting service worker registration failure after an update, clear site data for this domain once, close the tab, and reopen SaveFlow.</PanelCopy>
            </section>
        </>
    );
}

function AccountScreen() {
    return (
        <>
            <section className="flow-panel command-panel account-panel" aria-labelledby="account-title">
                <PanelCopy eyebrow="Local owner account" titleId="account-title" title="Secure this SaveFlow install">Accounts are local to this self-hosted install. Sync, billing, email reset, and OAuth are not enabled yet.</PanelCopy>
                <button id="refresh-account" type="button">Refresh Account</button>
            </section>
            <section className="status-grid metric-grid two-column" aria-label="Account status">
                <article className="status-card metric-card"><h2>Session</h2><p id="account-status" aria-live="polite">Checking account...</p></article>
                <article className="status-card metric-card"><h2>Local Data</h2><p id="account-summary">Log in to view local ownership counts.</p></article>
            </section>
            <AccountPanel id="register-panel" titleId="register-title" eyebrow="First setup" title="Create the owner account" copy="This form is only available until the first active local account exists.">
                <form id="register-form" className="history-form form-surface">
                    <label>Email <input name="email" type="email" autoComplete="email" required /></label>
                    <label>Username <input name="username" type="text" autoComplete="username" required minLength="3" maxLength="40" /></label>
                    <label>Display name <input name="display_name" type="text" autoComplete="name" required maxLength="120" /></label>
                    <label>Password <input name="password" type="password" autoComplete="new-password" required minLength="10" /></label>
                    <button type="submit">Create Account</button>
                </form>
            </AccountPanel>
            <AccountPanel id="login-panel" titleId="login-title" eyebrow="Login" title="Open your local account" copy="Use the owner email or username configured for this SaveFlow install.">
                <form id="login-form" className="history-form form-surface">
                    <label>Email or username <input name="login" type="text" autoComplete="username" required /></label>
                    <label>Password <input name="password" type="password" autoComplete="current-password" required /></label>
                    <button type="submit">Log In</button>
                    <a className="button-link secondary" href="/reset-password.html">Reset Password</a>
                </form>
            </AccountPanel>
            <AccountPanel id="profile-panel" titleId="profile-title" eyebrow="Profile" title="Account details" copy="These details stay on this SaveFlow install.">
                <form id="profile-form" className="history-form form-surface">
                    <label>Email <input name="email" type="email" autoComplete="email" required /></label>
                    <label>Username <input name="username" type="text" autoComplete="username" required minLength="3" maxLength="40" /></label>
                    <label>Display name <input name="display_name" type="text" autoComplete="name" required maxLength="120" /></label>
                    <button type="submit">Save Profile</button>
                </form>
            </AccountPanel>
            <AccountPanel id="password-panel" titleId="password-title" eyebrow="Password" title="Change password" copy="Changing the password signs out active sessions.">
                <form id="password-form" className="history-form form-surface">
                    <label>Current password <input name="current_password" type="password" autoComplete="current-password" required /></label>
                    <label>New password <input name="new_password" type="password" autoComplete="new-password" required minLength="10" /></label>
                    <button type="submit">Change Password</button>
                </form>
            </AccountPanel>
            <section id="logout-panel" className="flow-panel command-panel account-panel" aria-labelledby="logout-title" hidden>
                <PanelCopy eyebrow="Session control" titleId="logout-title" title="Log out of this browser">Downloads and local data remain stored on this SaveFlow install.</PanelCopy>
                <button id="logout-button" type="button">Log Out</button>
            </section>
            <ResultsSection id="recovery-panel" titleId="recovery-title" title="Recovery Codes" statusId="recovery-status" status="Recovery codes are shown once after generation." hidden>
                <button id="generate-recovery-codes" type="button">Generate Codes</button>
                <div id="recovery-codes" className="reading-content" hidden />
            </ResultsSection>
            <ResultsSection id="sessions-panel" titleId="sessions-title" title="Active Sessions" statusId="sessions-status" status="Loading sessions..." hidden>
                <div className="history-actions">
                    <button id="refresh-sessions" type="button">Refresh Sessions</button>
                    <button id="revoke-other-sessions" type="button">Revoke Others</button>
                </div>
                <div id="sessions-list" className="history-list result-list" />
            </ResultsSection>
        </>
    );
}

function InspectorScreen() {
    return (
        <>
            <section className="flow-panel command-panel inspector-hero" aria-labelledby="inspector-title">
                <div className="panel-copy">
                    <p className="eyebrow">Completed job</p>
                    <h2 id="inspector-title">Media details and exports</h2>
                    <p id="inspector-summary" aria-live="polite">Loading job metadata...</p>
                </div>
                <div id="inspector-thumb" className="inspector-thumb" aria-hidden="true" />
            </section>
            <section className="status-grid metric-grid two-column" aria-label="Inspection details">
                <article className="status-card metric-card inspector-card"><h2>Media</h2><ul id="media-details" className="meta-list"><li><span>Status:</span> Loading</li></ul></article>
                <article className="status-card metric-card inspector-card">
                    <h2>Exports</h2>
                    <p id="workspace-pref-status" className="hint">Loading workspace defaults...</p>
                    <div className="export-actions">
                        <button type="button" data-export="metadata-json">Metadata JSON</button>
                        <button type="button" data-export="metadata-txt">Metadata TXT</button>
                        <button type="button" data-export="caption-txt">Caption TXT</button>
                        <button type="button" data-export="bundle-zip">Bundle ZIP</button>
                        <button type="button" data-package="video_caption_zip">Video + Caption</button>
                        <button type="button" data-package="video_thumbnail_zip">Video + Thumbnail</button>
                        <button type="button" data-package="audio_metadata_zip">Audio + Metadata</button>
                        <button type="button" data-package="image_set_zip">Image Set ZIP</button>
                    </div>
                    <p id="export-status" aria-live="polite">Choose an export after the job details load.</p>
                    <div id="export-result" className="export-result" />
                </article>
            </section>
            <section className="history-results section-surface results-surface" aria-labelledby="downloads-title">
                <h2 id="downloads-title">Registered Downloads</h2>
                <div id="download-list" className="history-list result-list" />
            </section>
            <section className="history-results section-surface results-surface reading-panel" aria-labelledby="reading-title">
                <h2 id="reading-title">Reading Mode</h2>
                <div id="reading-content" className="reading-content"><p>Loading readable metadata...</p></div>
                <div className="export-actions">
                    <button id="add-inspector-collection" type="button">Add to Collection</button>
                    <a className="button-link secondary" href="/collections.html">Open Collections</a>
                </div>
            </section>
        </>
    );
}

function ResetPasswordScreen() {
    return (
        <>
            <section className="flow-panel command-panel recovery-panel" aria-labelledby="reset-title">
                <PanelCopy eyebrow="Account recovery" titleId="reset-title" title="Recover local access">Email reset uses this server's configured mail delivery. Recovery codes work locally when mail is unavailable.</PanelCopy>
                <a className="button-link secondary" href="/account.html">Back To Account</a>
            </section>
            <section className="status-card metric-card utility-card"><h2>Status</h2><p id="reset-status" aria-live="polite">Choose a recovery method.</p></section>
            <section className="status-grid metric-grid two-column" aria-label="Password recovery options">
                <article className="status-card metric-card"><h2>Email Reset Link</h2><form id="request-reset-form" className="history-form form-surface"><label>Email <input name="email" type="email" autoComplete="email" required /></label><button type="submit">Send Reset Link</button></form></article>
                <article className="status-card metric-card"><h2>Use Reset Link</h2><form id="complete-reset-form" className="history-form form-surface"><label>Reset token <input name="token" type="text" autoComplete="off" required /></label><label>New password <input name="new_password" type="password" autoComplete="new-password" required minLength="10" /></label><button type="submit">Change Password</button></form></article>
                <article className="status-card metric-card"><h2>Recovery Code</h2><form id="recovery-code-form" className="history-form form-surface"><label>Recovery code <input name="code" type="text" autoComplete="one-time-code" required /></label><label>New password <input name="new_password" type="password" autoComplete="new-password" required minLength="10" /></label><button type="submit">Use Recovery Code</button></form></article>
            </section>
        </>
    );
}

function OfflineScreen() {
    return (
        <section className="flow-panel command-panel" aria-labelledby="offline-title">
            <PanelCopy eyebrow="PWA fallback" titleId="offline-title" title="SaveFlow is offline">The app shell is available, but downloads, history, queue actions, and settings checks need a network connection to the SaveFlow server.</PanelCopy>
            <a className="button-link" href="/">Return to Workspace</a>
        </section>
    );
}

function PreferencesForm() {
    return (
        <form id="preferences-form" className="history-form form-surface">
            <h3>Default export choices</h3>
            <div className="checkbox-row">
                {['metadata-json', 'metadata-txt', 'caption-txt', 'bundle-zip'].map((value) => <label key={value}><input name="default_export_types" value={value} type="checkbox" /> {labelForPackage(value)}</label>)}
            </div>
            <label>Default package<PackageSelect name="default_package_type" includeBlank={false} /></label>
            <button type="submit">Save Defaults</button>
        </form>
    );
}

function RecipeForm() {
    return (
        <form id="recipe-form" className="history-form form-surface">
            <input type="hidden" name="id" />
            <h3>Download recipe</h3>
            <label>Name<input name="name" type="text" maxLength="120" placeholder="YouTube archive package" /></label>
            <div className="filter-row">
                <label>Platform<PlatformSelect /></label>
                <label>Package<PackageSelect name="preferred_package_type" includeBlank /></label>
                <label>Open inspector<Select name="auto_open_inspector" options={[['0', 'No'], ['1', 'Yes']]} /></label>
            </div>
            <div className="checkbox-row">
                {['metadata-json', 'metadata-txt', 'caption-txt', 'bundle-zip'].map((value) => <label key={value}><input name="preferred_export_types" value={value} type="checkbox" /> {labelForPackage(value)}</label>)}
            </div>
            <button type="submit">Save Recipe</button>
        </form>
    );
}

function PanelCopy({ eyebrow, titleId, title, children }) {
    return <div className="panel-copy"><p className="eyebrow">{eyebrow}</p><h2 id={titleId}>{title}</h2><p>{children}</p></div>;
}

function ResultsSection({ id, titleId, title, statusId, status, hidden = false, children }) {
    const childrenArray = React.Children.toArray(children);
    const actionChild = childrenArray[0];
    const rest = childrenArray.slice(1);

    return (
        <section id={id} className="history-results section-surface results-surface" aria-labelledby={titleId} hidden={hidden}>
            <div className="section-head section-head-actions">
                <h2 id={titleId}>{title}</h2>
                {actionChild}
            </div>
            <p id={statusId} aria-live="polite">{status}</p>
            {rest}
        </section>
    );
}

function AccountPanel({ id, titleId, eyebrow, title, copy, children }) {
    return <section id={id} className="flow-panel command-panel account-panel" aria-labelledby={titleId} hidden><PanelCopy eyebrow={eyebrow} titleId={titleId} title={title}>{copy}</PanelCopy>{children}</section>;
}

function SavedFilterBar({ scope }) {
    return (
        <div className="saved-filter-bar filter-toolbar" data-filter-scope={scope}>
            <input data-filter-name type="text" maxLength="120" placeholder="Filter name" />
            <button type="button" data-save-filter>Save Filter</button>
            <select data-filter-list><option value="">Saved filters</option></select>
            <button type="button" data-apply-filter>Apply</button>
        </div>
    );
}

function PlatformSelect({ name = 'platform', id }) {
    return <Select id={id} name={name} options={[['', 'All platforms'], ['youtube', 'YouTube'], ['tiktok', 'TikTok'], ['instagram', 'Instagram']]} />;
}

function PackageSelect({ name, includeBlank }) {
    const options = [
        ...(includeBlank ? [['', 'No package preference']] : []),
        ['bundle-zip', 'Bundle ZIP'],
        ['video_caption_zip', 'Video + Caption'],
        ['video_thumbnail_zip', 'Video + Thumbnail'],
        ['audio_metadata_zip', 'Audio + Metadata'],
        ['image_set_zip', 'Image Set ZIP'],
    ];
    return <Select name={name} options={options} />;
}

function Select({ name, id, options }) {
    return <select id={id} name={name}>{options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>;
}

function Footer() {
    return <footer className="app-footer"><span>SaveFlow by Aliiq</span><a href="https://aliiq.me" rel="noreferrer">aliiq.me</a></footer>;
}

function loadA11y(defaults) {
    try {
        return { ...defaults, ...JSON.parse(localStorage.getItem('saveflow:accessibility') || '{}') };
    } catch {
        return { ...defaults };
    }
}

function labelForPackage(value) {
    return {
        'metadata-json': 'Metadata JSON',
        'metadata-txt': 'Metadata TXT',
        'caption-txt': 'Caption TXT',
        'bundle-zip': 'Bundle ZIP',
    }[value] || value;
}

function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
}

function beep() {
    try {
        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.connect(gain);
        gain.connect(context.destination);
        gain.gain.value = 0.04;
        oscillator.frequency.value = 880;
        oscillator.start();
        oscillator.stop(context.currentTime + 0.12);
    } catch {
        // Optional audio feedback only.
    }
}
